import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../../../context/AuthContext';
import api from '../../../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './SlotRequestForm.css';

const SlotRequestForm = ({ interviewerId, onSuccess, onCancel, defaultInterviewType = '' }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [interviewer, setInterviewer] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    interviewType: defaultInterviewType || '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Fetch interviewer details
  useEffect(() => {
    const fetchInterviewer = async () => {
      try {
        const response = await api.get(`/api/users/${interviewerId}`);
        setInterviewer(response.data);
      } catch (err) {
        console.error('Error fetching interviewer:', err);
        toast.error('Failed to load interviewer details');
      }
    };

    fetchInterviewer();
  }, [interviewerId]);

  // Load timezones
  useEffect(() => {
    const loadTimezones = () => {
      const timezoneList = Intl.supportedValuesOf('timeZone');
      setTimezones(timezoneList);
      
      // Set user's timezone as default
      try {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setSelectedTimezone(userTimezone);
      } catch (e) {
        console.error('Could not detect timezone:', e);
        setSelectedTimezone('UTC');
      }
    };

    loadTimezones();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.interviewType) {
      errors.interviewType = 'Interview type is required';
    }
    
    if (!formData.preferredDate) {
      errors.preferredDate = 'Preferred date is required';
    }
    
    if (!formData.preferredTime) {
      errors.preferredTime = 'Preferred time is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || loading) {
      return;
    }
    
    try {
      setLoading(true);
      
      const requestData = {
        interviewerId,
        interviewType: formData.interviewType,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        timezone: selectedTimezone,
        message: formData.message || '',
      };
      
      await api.post('/api/slot-requests', requestData);
      
      toast.success('Slot request submitted successfully!');
      if (onSuccess) onSuccess();
      
    } catch (err) {
      console.error('Error submitting slot request:', err);
      const errorMessage = err.response?.data?.message || 'Failed to submit slot request';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!interviewer) {
    return <div>Loading interviewer details...</div>;
  }

  return (
    <div className="slot-request-form">
      <h2>Request a Slot with {interviewer.name}</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="interviewType">Interview Type</label>
          <select
            id="interviewType"
            name="interviewType"
            className={`form-control ${formErrors.interviewType ? 'is-invalid' : ''}`}
            value={formData.interviewType}
            onChange={handleChange}
          >
            <option value="">Select interview type</option>
            <option value="DSA">DSA</option>
            <option value="System Design">System Design</option>
            <option value="Behavioral/Leadership">Behavioral/Leadership</option>
          </select>
          {formErrors.interviewType && (
            <div className="invalid-feedback">{formErrors.interviewType}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="preferredDate">Preferred Date</label>
          <input
            type="date"
            id="preferredDate"
            name="preferredDate"
            className={`form-control ${formErrors.preferredDate ? 'is-invalid' : ''}`}
            min={new Date().toISOString().split('T')[0]}
            value={formData.preferredDate}
            onChange={handleChange}
          />
          {formErrors.preferredDate && (
            <div className="invalid-feedback">{formErrors.preferredDate}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="preferredTime">Preferred Time</label>
          <input
            type="time"
            id="preferredTime"
            name="preferredTime"
            className={`form-control ${formErrors.preferredTime ? 'is-invalid' : ''}`}
            value={formData.preferredTime}
            onChange={handleChange}
          />
          {formErrors.preferredTime && (
            <div className="invalid-feedback">{formErrors.preferredTime}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="timezone">Timezone</label>
          <select
            id="timezone"
            className="form-control"
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(e.target.value)}
          >
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="message">Additional Message (Optional)</label>
          <textarea
            id="message"
            name="message"
            className="form-control"
            rows="3"
            placeholder="Any specific topics you'd like to cover or additional information..."
            value={formData.message}
            onChange={handleChange}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SlotRequestForm;
